import { format, isAfter, isSameDay } from 'date-fns';
import _ from 'lodash';
import { useTranslation } from 'next-i18next';
import React, { useEffect, useRef, useState } from 'react';
import { shallowEqual } from 'react-redux';
import { Slot } from '../../../../../../interfaces/interfaces';
import { PresentationComponentInterface } from '../../../../../../interfaces/pagebuilder';
import { RootState } from '../../../../../../reduxToolkit/store';
import { useSelector } from '../../../../../../reduxToolkit/typedRedux';
import { formatUTCDate } from '../../../../../../services/helper';
import PublicPresentationComponent from './PublicPresentation/PublicPresentationComponent';

type Status = 'live' | 'upcoming' | 'old';
interface FormattedSlots {
  [key: string]: Record<string, Slot[]>;
}

interface OrderedSlots {
  [date: string]: FormattedSlots;
}

const PresentationCardView = ({ uiComponent }: { uiComponent?: PresentationComponentInterface }) => {
  const { event, slots } = useSelector(
    ({ common }: RootState) => ({ event: common.event, slots: common.slots, favoriteSlots: common.favoriteSlots }),
    shallowEqual
  );

  const { t } = useTranslation();
  const ref = useRef<HTMLDivElement>(null);

  const [orderedSlots, setOrderedSlots] = useState<OrderedSlots>();

  function giveRealStatus(status: Status) {
    switch (status) {
      case 'live':
        return t('event:presentation-live');
      case 'upcoming':
        return t('event:presentation-upcoming');
      case 'old':
        return t('event:presentation-old');
    }
  }

  if (!event) return <></>;

  function makeGroupName(slot: Slot) {
    const dateFormat = isSameDay(new Date(slot.datetime_start), new Date(slot.datetime_end)) ? 'HH:mm' : 'dd.MM.yyyy HH:mm';
    if (uiComponent?.groupByStartTime) return `${formatUTCDate(new Date(slot.datetime_start), dateFormat)}`;
    return `${formatUTCDate(new Date(slot.datetime_start), dateFormat)} - ${formatUTCDate(new Date(slot.datetime_end), dateFormat)}`;
  }

  function groupSlotsByTime(slots: Slot[]) {
    /**
     * We sort here again as slots are able to overlap
     * e.g.
     * slot 18:00 - 18:30
     * should be before
     * slot 18:00 - 19:00
     */
    return slots
      ?.sort((a, b) => parseInt(b?.datetime_start) - parseInt(a?.datetime_start))
      .reduce((j: Record<string, Slot[]>, slot) => {
        const group = makeGroupName(slot);
        j.hasOwnProperty(group) ? j[group].push(slot) : (j[group] = [slot]);
        return j;
      }, {});
  }

  useEffect(() => {
    if (
      slots?.filter(
        (slot) => !uiComponent?.selectedPresentations || uiComponent.selectedPresentations.length === 0 || uiComponent?.selectedPresentations.includes(slot._id)
      ).length > 0
    ) {
      const sortedSlots = [
        ...slots
          .filter(
            (slot) =>
              !uiComponent?.selectedPresentations || uiComponent.selectedPresentations.length === 0 || uiComponent?.selectedPresentations.includes(slot._id)
          )
          .filter((slot) => !uiComponent?.hidePastPresentations || isAfter(new Date(slot.datetime_end), new Date())),
      ].sort((a, b) => parseInt(b?.datetime_start) - parseInt(a?.datetime_start));

      const groupedByDate = _.groupBy(sortedSlots, (slot: Slot) => format(new Date(slot.datetime_start), 'dd.MM.yyy'));

      const output: OrderedSlots = {};

      Object.entries(groupedByDate).map(([key, v]) => {
        const onlyLive = v.filter((slot) => slot.status === 'live');
        const onlyUpcoming = v.filter((slot) => slot.status === 'upcoming');
        const onlyOld = v.filter((slot) => slot.status === 'old');

        const live = groupSlotsByTime(onlyLive);
        const upcoming = groupSlotsByTime(onlyUpcoming);
        const old = groupSlotsByTime(onlyOld);

        output[key] = { live, upcoming, old };
      });

      setOrderedSlots(output);
    }
  }, [slots, uiComponent?.selectedPresentations]);

  const ROW_LENGTH = 2;
  const PRESENTATIONS_IN_BOX = 2;

  function formatSlots(slots: Slot[]): Slot[] {
    if (!event?.template.stackedPresentations) return slots.sort((a, b) => a.sortIndex - b.sortIndex);

    const stacked: Slot[] = [];
    slots?.forEach((slot, index) => {
      const isForStacked = Array.from({ length: PRESENTATIONS_IN_BOX })
        .map((_, i) => index !== 0 && index % (ROW_LENGTH + i) === 0)
        .some((e) => e);
      const lastItem = stacked[stacked.length - 1];
      if (!isForStacked) stacked.push(slot);
      else if (lastItem) {
        if (Array.isArray(lastItem)) {
          lastItem.push(slot);
        }
      }
    });
    stacked.filter((slot) => !Array.isArray(slot)).sort((a, b) => a.sortIndex - b.sortIndex);
    return stacked;
  }

  const _width = ref.current?.offsetWidth ?? 1000;

  return (
    <div className="flex flex-col w-full" ref={ref} data-force-color="true">
      {orderedSlots &&
        Object.entries(orderedSlots).map(([key, value], idx) => (
          <div
            className="mb-4 w-full text-slate-800"
            key={`pres-sec-${key}`}
            style={{
              color: uiComponent?.styling?.textColor,
            }}>
            {uiComponent?.showDate && <p className="mt-4 text-2xl font-bold text-left">{key}</p>}
            {Object.entries(value).map(([status, v]) => (
              <div key={status}>
                {Object.entries(v).map(([time, slots], index) => (
                  <React.Fragment key={`slot-${time}-${key}-${status}-${index}`}>
                    {uiComponent?.showPresentationStatus && index === 0 && <p className="text-lg">{giveRealStatus(status as Status)}</p>}
                    <div className="justify-center w-full">
                      {uiComponent?.showTime && <p className="text-lg">{time}</p>}
                      <div
                        className={`w-full grid grid-cols-1 ${_width < 700 ? '@lg:grid-cols-1' : _width < 1000 ? '@lg:grid-cols-2' : '@lg:grid-cols-3'} gap-4`}>
                        {formatSlots(slots).map((slot) => (
                          <PublicPresentationComponent
                            key={slot._id}
                            slot={slot}
                            boxClassName="h-full"
                            spotlight={uiComponent?.spotlightPresentations?.includes(slot.meetingroom?._id)}
                            presentationTextColor={uiComponent?.customStyling?.presentationTextColor}
                            showStartEndTime={uiComponent?.showStartEndTime}
                          />
                        ))}
                      </div>
                    </div>
                  </React.Fragment>
                ))}
              </div>
            ))}
          </div>
        ))}
    </div>
  );
};

export default PresentationCardView;
